import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap';
import Select from 'react-select';
import { asyncWrap } from '../../utils/utils';
import { useHistory } from 'react-router';

import L from 'leaflet';


const AddLead = () => {
  const [submitting, setSubmitting] = useState(false);
  const [selectProduct, setSelectProduct] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [markerInstance, setMarkerInstance] = useState(null);

  const history = useHistory();

  const DEFAULT_FALLBACK = { lat: 23.0225, lng: 72.5714 }; // Ahmedabad

  useEffect(() => {
    getSkusDetails();
  }, []);

  const getSkusDetails = async () => {
    const [error, result] = await asyncWrap(axios.get('api/skus'));
    if (!result) return console.log(error);
    setSelectProduct(result.data?.data);
  };

  // ----------------------------------------------
  // Formik config
  // ----------------------------------------------
  const initialValues = {
    projectName: '',
    ownerContact: '',
    projectLocation: '',
    locationData: null,
    quantity: '',
    productCategory: '',
    retailerName: '',
    skuid: '',
  };

  const validationSchema = Yup.object({
    projectName: Yup.string().required('Project name is required'),
    ownerContact: Yup.string().required('Owner contact is required'),
    projectLocation: Yup.string().required('Project location required'),
    quantity: Yup.number()
      .required('Quantity is required')
      .positive('Must be positive')
      .integer('Must be a whole number'),
    productCategory: Yup.string().required('Product category is required'),
    retailerName: Yup.string().required('Retailer name is required'),
    skuid: Yup.string().required('SKU ID is required'),
  });

  // ----------------------------------------------
  // Submit form
  // ----------------------------------------------
  const handleSubmit = async (values, resetForm) => {
    setSubmitting(true);

    const payload = {
      NameOfProject: values.projectName,
      ContactOfOwner: values.ownerContact,
      LocationOfProject: values.projectLocation,
      TotalQtyRequirement: values.quantity,
      ProductCategory: values.productCategory,
      FromWhichRetailerYouBought: values.retailerName,
      SkuID: values.skuid,
      // Latitude: values.locationData?.lat,
      // Longitude: values.locationData?.lng
    };

    try {
      const result = await axios.post('/lead', payload);

      if (result?.data?.success) {
        Swal.fire('Success', 'Lead added successfully!', 'success');
        history.push('/lead');
        resetForm();
      } else {
        Swal.fire('Error', 'Something went wrong!', 'error');
      }
    } catch (error) {
      Swal.fire(
        'Error',
        error?.response?.data?.message || 'Something went wrong!',
        'error'
      );
    }

    setSubmitting(false);
  };

  // ----------------------------------------------
  // OPEN MAP MODAL
  // ----------------------------------------------
  const openMap = (setFieldValue, values) => {
    setModalOpen(true);

    setTimeout(() => {
      let start = DEFAULT_FALLBACK;

      if (values.locationData) {
        start = values.locationData;
      } else {
        navigator.geolocation.getCurrentPosition(
          pos => {
            start = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            loadMap(start);
          },
          () => loadMap(start)
        );
        return;
      }

      loadMap(start);
    }, 350);
  };

  // ----------------------------------------------
  // LOAD LEAFLET MAP
  // ----------------------------------------------
  const loadMap = position => {
    if (mapInstance) return;

    const map = L.map('leafletMap').setView([position.lat, position.lng], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(
      map
    );

    const marker = L.marker([position.lat, position.lng], {
      draggable: true,
    }).addTo(map);

    setMapInstance(map);
    setMarkerInstance(marker);
  };

  // ----------------------------------------------
  // CLEANUP MAP WHEN MODAL CLOSES
  // ----------------------------------------------
  const cleanupMap = () => {
    if (mapInstance) {
      mapInstance.remove();
      setMapInstance(null);
      setMarkerInstance(null);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => handleSubmit(values, resetForm)}
      >
        {({ values, errors, touched, setFieldValue }) => {
          // -----------------------------------------------------
          // LISTEN FOR "save-location" EVENT (IMPORTANT)
          // -----------------------------------------------------
          useEffect(() => {
            const handler = () => {
              if (markerInstance) {
                const { lat, lng } = markerInstance.getLatLng();

                setFieldValue('projectLocation', `${lat}, ${lng}`);
                setFieldValue('locationData', { lat, lng });

                Swal.fire(
                  'Location Saved!',
                  `Latitude: ${lat}, Longitude: ${lng}`,
                  'success'
                );

                cleanupMap();
                setModalOpen(false);
              }
            };

            window.addEventListener('save-location', handler);
            return () => window.removeEventListener('save-location', handler);
          }, [markerInstance]);

          return (
            <Form style={{ padding: '20px' }}>
              <h2 style={{ fontWeight: 700 }}>Add Lead</h2>
              <p>Enter project and owner details below</p>

              {/* ---------------- SKU SELECT ---------------- */}
              <Col xs='12' md='9' className='p-0 mb-3'>
                <Label>Select SKU</Label>

                <Select
                  options={selectProduct?.map(item => ({
                    label: item.skuUniqueId,
                    value: item.skuId,
                  }))}
                  value={
                    selectProduct
                      ?.map(item => ({
                        label: item.skuUniqueId,
                        value: item.skuId,
                      }))
                      .find(option => option.value === values.skuid) || null
                  }
                  onChange={opt => setFieldValue('skuid', opt?.value)}
                  styles={{
                    menu: base => ({
                      ...base,
                      zIndex: 9, // 👈 dropdown menu on top
                    }),
                    menuPortal: base => ({ ...base, zIndex: 9 }), // ⭐ FIX: show on top

                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#3A69DD' : '#B0B0B0',
                      padding: '5px',
                      zIndex: 9,
                    }),

                    valueContainer: baseStyles => ({
                      ...baseStyles,
                      padding: '0px',
                    }),
                  }}
                />

                {errors.skuid && touched.skuid && (
                  <div className='text-danger'>{errors.skuid}</div>
                )}
              </Col>

              {/* ---------------- REST FIELDS ---------------- */}
              <Col xs='12' md='9' className='p-0 mb-3'>
                <FormGroup floating row>
                  <Field
                    name='projectName'
                    placeholder='Enter Project Name'
                    as={Input}
                    invalid={!!(touched.projectName && errors.projectName)}
                  />
                  <Label>Name of Project</Label>
                  <ErrorMessage
                    name='projectName'
                    component={FormFeedback}
                    style={{ display: 'block' }}
                  />
                </FormGroup>
              </Col>

              <Col xs='12' md='9' className='p-0 mb-3'>
                <FormGroup floating row>
                  <Field
                    name='ownerContact'
                    placeholder='Enter Owner Contact'
                    as={Input}
                    invalid={!!(touched.ownerContact && errors.ownerContact)}
                  />
                  <Label>Contact of Owner</Label>
                  <ErrorMessage
                    name='ownerContact'
                    component={FormFeedback}
                    style={{ display: 'block' }}
                  />
                </FormGroup>
              </Col>

              <Col xs='12' md='9' className='p-0 mb-3'>
                <FormGroup floating row>
                  <Field
                    name='quantity'
                    type='number'
                    placeholder='Enter Total Quantity'
                    as={Input}
                    invalid={!!(touched.quantity && errors.quantity)}
                  />
                  <Label>Total Qty Requirement</Label>
                  <ErrorMessage
                    name='quantity'
                    component={FormFeedback}
                    style={{ display: 'block' }}
                  />
                </FormGroup>
              </Col>

              <Col xs='12' md='9' className='p-0 mb-3'>
                <FormGroup floating row>
                  <Field
                    name='productCategory'
                    placeholder='Enter Product Category'
                    as={Input}
                    invalid={
                      !!(touched.productCategory && errors.productCategory)
                    }
                  />
                  <Label>Product Category</Label>
                  <ErrorMessage
                    name='productCategory'
                    component={FormFeedback}
                    style={{ display: 'block' }}
                  />
                </FormGroup>
              </Col>

              <Col xs='12' md='9' className='p-0 mb-3'>
                <FormGroup floating row>
                  <Field
                    name='retailerName'
                    placeholder='Enter From Which Retailer You Bought'
                    as={Input}
                    invalid={!!(touched.retailerName && errors.retailerName)}
                  />
                  <Label>Enter From Which Retailer You Bought </Label>
                  <ErrorMessage
                    name='retailerName'
                    component={FormFeedback}
                    style={{ display: 'block' }}
                  />
                </FormGroup>
              </Col>

              {/* <Col xs="12" md="9" className="p-0 mb-3">
                <FormGroup floating row>
                  <Field
                    name="projectLocationText"
                    placeholder="Enter Project Address (Optional)"
                    as={Input}
                  />
                  <Label>Project Address (Optional)</Label>
                </FormGroup>
              </Col> */}

              {/* ---------------- LOCATION PICKER ---------------- */}
              <Col xs='12' md='9' className='p-0 mb-3'>
                <FormGroup floating row>
                  <Input
                    disabled
                    value={values.projectLocation}
                    readOnly
                    placeholder='Latitude, Longitude'
                  />
                  <Label>Project Location</Label>
                </FormGroup>

                <Button
                  color='primary'
                  onClick={() => openMap(setFieldValue, values)}
                >
                  📍 Pick Location on Map
                </Button>

                {errors.projectLocation && touched.projectLocation && (
                  <div className='text-danger mt-1'>
                    {errors.projectLocation}
                  </div>
                )}
              </Col>

              {/* SUBMIT */}
              <Button type='submit' color='primary' disabled={submitting}>
                {submitting ? 'Saving...' : 'Add Lead'}
              </Button>
            </Form>
          );
        }}
      </Formik>

      {/* --------------------------------------------
          MAP PICKER MODAL
      --------------------------------------------- */}
      <Modal
        isOpen={modalOpen}
        toggle={() => {
          cleanupMap();
          setModalOpen(false);
        }}
        size='lg'
        style={{zIndex: 99999}}
      >
        <ModalHeader>Pick Project Location</ModalHeader>

        <ModalBody>
          <div
            id='leafletMap'
            style={{
              height: '400px',
              width: '100%',
              borderRadius: '8px',
            }}
          ></div>
        </ModalBody>

        <ModalFooter>
          <Button
            color='secondary'
            onClick={() => {
              cleanupMap();
              setModalOpen(false);
            }}
          >
            Cancel
          </Button>

          <Button
            color='primary'
            onClick={() => {
              const e = new Event('save-location');
              window.dispatchEvent(e);
            }}
          >
            Save Location
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AddLead;
